import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import { STATUSES_GROUPS, TRACKING_GROUPS } from '../constants/deliveryTracking';
import { IDeliveryRequest, IDeliveryTrackingFilters, ISelectedDeliveryRequest } from '../types/deliveryTracking';
import { defaultPagination } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { CoordsBounds, IPagination } from 'src/types/main';
import { getCancelToken } from 'src/utils/helpers';
import MapStore from 'src/view/components/Map/store';
import logger from '~/services/logger';

class DeliveryTrackingStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;
  cancelStack: {
    [key: string]: Canceler;
  } = {};

  public mapStore = new MapStore();

  @observable pagination: IPagination<IDeliveryRequest> = defaultPagination;
  @observable selected: ISelectedDeliveryRequest = {
    request: undefined,
    details: {
      takergo_timeline: [],
      takergo_request_timeline: [],
    },
    loading: false,
  };
  @observable pages: number[] = [];
  @observable filters: IDeliveryTrackingFilters = {
    search: '',
    group: TRACKING_GROUPS.ALL,
  };

  @action setFilters = (filters: Partial<IDeliveryTrackingFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchRequestsList(1);
  };

  @action setSelectedRequest = (request?: IDeliveryRequest) => {
    if (!request) return;

    this.selected = { ...this.selected, request };
    this.setCamera([
      [request.branch.latitude, request.branch.longitude],
      [request.customer.latitude, request.customer.longitude],
    ]);
  };

  @action clearSelectedRequest = () => {
    this.selected = { ...this.selected, request: undefined };
    this.mapStore.reset();
  };

  @action fetchRequestsList = async (page: number = this.pagination.current_page + 1, requestId?: string | number) => {
    try {
      const cancelId = 'fetchRequests';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.loading = true;
      this.pagination.refreshing = page <= 1;

      let request: IDeliveryRequest | undefined = undefined;

      if (requestId) {
        request = await client.get(`v3/delivery-tracking/${requestId}`).catch((error: any) => {
          if (error.response?.status === 404) {
            return;
          }
          throw error;
        });
      }

      // TODO: add request id parameter to get requests in specific page (need implementation from backend)
      const pagination: IPagination<IDeliveryRequest> = await client.get('v3/delivery-tracking', {
        params: {
          status: STATUSES_GROUPS[this.filters.group],
          search: this.filters.search,
          page,
        },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });
      this.pagination = { ...pagination, data: page > 1 ? [...this.pagination.data, ...pagination.data] : pagination.data };
      this.pages = page > 1 ? [...this.pages, page] : [page];

      const isExistingRequest = this.pagination.data.some((_request) => _request.id === request?.id);
      if (requestId && !isExistingRequest) {
        history.push(SCREENS.DELIVERY_TRACKING());
        this.clearSelectedRequest();
      } else {
        this.setSelectedRequest(request);
      }

      this.pagination.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.loading = false;
      logger.error({ error, place: 'DeliveryTrackingStore fetchRequestsList' });
    }
  };

  @action fetchRequestDetails = async (requestId: string | number) => {
    try {
      this.selected.loading = true;

      const { takergo_timeline, takergo_request_timeline } = await client.get(`v3/delivery-tracking/${requestId}`);
      this.selected.details = {
        takergo_timeline,
        takergo_request_timeline: Object.values(takergo_request_timeline), // WARNING: can be received associative array from back
      };

      this.selected.loading = false;
    } catch (error: any) {
      this.selected.loading = false;
      logger.error({ error, place: 'DeliveryTrackingStore fetchRequestDetails' });
    }
  };

  @action requestDelivery = async (orderId: string | number) => {
    try {
      this.selected.loading = true;
      await client.post(`/v3/orders/${orderId}/request_delivery`);
      this.selected.loading = false;
    } catch (error: any) {
      this.selected.loading = false;
      logger.error({ error, place: 'DeliveryTrackingStore requestDelivery', showModal: true });
    }
  };

  @action cancelDelivery = async (orderId: string | number) => {
    try {
      this.selected.loading = true;
      await client.post(`/v3/orders/${orderId}/cancel_delivery`);
      this.selected.loading = false;
    } catch (error: any) {
      this.selected.loading = false;
      logger.error({ error, place: 'DeliveryTrackingStore cancelDelivery', showModal: true });
    }
  };

  @action setCamera = (bounds: CoordsBounds) => {
    this.mapStore.fitMap(bounds, undefined, { height: 200 });
  };
}

export const deliveryTrackingStore = new DeliveryTrackingStore();
